// ============================================================================
// bunchaBullshiiii.ts — GOOP ZONE "Buncha Bullshiiii" Database
// In-run passive items found during gameplay
// Lives in: client/src/game/bunchaBullshiiii.ts
//
// BETA 19.1 — REFACTOR v2 (Megabonk-correct stacking model).
//
// Three rules per Dylan + Megabonk research:
//   1. Every item does exactly ONE buff (no multi-stat stacking).
//   2. No random-pool gacha items ("+X OR +Y OR +Z per pick").
//   3. Stacking is allowed (Megabonk model) — each pickup gives the SAME
//      fixed per-copy value. Card text shows the per-pickup amount only,
//      never "per stack" wording.
//
// Three stacking buckets:
//   A. FREE STACK (maxStacks: 99) — pure stat buffs. Each pickup adds the
//      flat per-copy value. Example: Big Ol' Boy = +30 max HP per pickup.
//      Engine multiplies statModifiers[].value × slot.stacks in
//      bunchaBullshiiiiSystem.recalculateStats.
//   B. CAPPED STACK (maxStacks: 3-5) — mechanic items where stacking
//      makes sense (proc chance, radius, duration). Engine already reads
//      slot.stacks in per-item apply paths in itemsAdapter.ts.
//   C. UNIQUE (maxStacks: 1) — binary-mechanic items where stacking
//      doesn't make sense (Sheeesh revive, Vampire Fang synergy, etc.).
//
// Shelved (status: 'shelved'): 8 tier-bloat duplicates (UC04 / UC06 /
// E01-E04 / L01 / L02) whose sprites were just reuses of base-tier
// items. Megabonk-style luck-weighted rarity rolling replaces their
// role — picking up Big Ol' Boy 3 times stacks naturally; you don't
// need a separate "Big Ol' Boy III" item.
// ============================================================================

import { rollRng } from './rng'; // BETA 20.93 — seeded balance rolls (reproducible item rarity rolls)

// --- Type Definitions ---

export type ItemRarity = 'common' | 'uncommon' | 'rare' | 'epic' | 'legendary';

export type SynergyTag =
  | 'damage'
  | 'health'
  | 'luck'
  | 'speed'
  | 'xp'
  | 'defense'
  | 'chaos'
  | 'utility';

export type TriggerType =
  | 'passive'
  | 'on_hit'
  | 'on_kill'
  | 'on_damage'
  | 'on_move'
  | 'on_chest'
  | 'on_crit';

export interface StatModifier {
  stat: string;
  value: number;
  isPercent: boolean;
}

export interface ItemEffect {
  trigger: TriggerType;
  description: string;
  effectId: string;
  chance: number;
}

// BETA 19.1 — BuffDimension preserved as a type definition for backward
// compatibility with itemsAdapter.ts code paths that check for it, but
// NO ITEM in the active roster uses dimensions anymore.
export interface BuffDimension {
  statField: string;
  label: string;
  bonusPerStack: number;
  unit: 'flat' | 'pct' | 'sec' | 'wu' | 'mult';
  weight: number;
  playerStat?: PlayerStatKey;
}

export type PlayerStatKey =
  | 'maxHp' | 'moveSpeed'
  | 'rangeBonus' | 'pickupRange'
  | 'damageBonus' | 'attackSpeedBonus'
  | 'critChance' | 'critDamage' | 'lifesteal'
  | 'armor' | 'evasion' | 'thorns'
  | 'luck'
  | 'hpRegen' | 'shieldRegen'
  | 'eliteDamageBonus' | 'projectileSpeedBonus' | 'attackAreaBonus'
  | 'projectileBounceBonus' | 'projectileCountBonus' | 'knockbackBonus'
  | 'xpMultiplier' | 'goldMultiplier'
  | 'maxShieldBonus' | 'damageReduction';

export interface BullshiiiiItem {
  id: string;
  name: string;
  description: string;
  flavorText: string;
  rarity: ItemRarity;
  synergy: SynergyTag;
  iconUrl: string;
  starterPool: boolean;
  stackable: boolean;
  maxStacks: number;
  statModifiers: StatModifier[];
  effects: ItemEffect[];
  megabonkRef: string;
  hasWeapon: boolean;
  status: 'active' | 'shelved';
  unlockCondition?: {
    type: string;
    value: number;
    description: string;
  };
  buffDimensions?: BuffDimension[];
  // BETA 21.75 — editable effect magnitudes for the Balance Dashboard. Optional;
  // only present on MIGRATED items. Handlers read a value via tuneOf(id,key,fallback);
  // if absent, the hardcoded fallback is used so behavior is byte-identical.
  tuning?: Record<string, number>;
  // Card-text template with {key} placeholders expanded from tuning (+ {stat} for the
  // statModifier line). If absent, describeItem() returns the static description.
  descriptionTemplate?: string;
}

// --- Icon URLs ---
const ICON_URLS = {
  C01: '/items/C01_Hutch%27s%20Sausage%20Fingers.png',
  C02: '/items/C02_Big%20Ol%27%20Boy.png',
  C03: '/items/C03_Bungulator%27s%20Wrench.png',
  C04: '/items/C04_Hutch%27s%20Lucky%20Horseshoe.png',
  C05: '/items/C05_XP%20Juice.png',
  C06: '/items/C06_Peanut%27s%20Underwear.png',
  C07: '/items/C07_Sticky%20Fingers.png',
  C08: '/items/C08_Pre%20Workout.png',
  C09: '/items/C09_Little%20Black%20Dress%20%28TJ%20Maxx%29.png',
  C10: '/items/C10_Stim%20Pod.png',
  UC03: '/items/UC03_Adrenaline%20Pod.png',   // BETA 21.84 — own art (was sharing C10's Stim Pod icon → identical cards)
  R01: '/items/R01_Cloakzy%27s%20Championship%20Belt.png',
  R02: '/items/R02_Voogoo%27s%20Rabbit%20Foot.png',
  R03: '/items/R03_The%20Wyfe%20Beater.png',
  R05: '/items/R05_Peanut%27s%20Blood%20Bag.png',
  R06: '/items/R06_Smashley%27s%20Bubble%20Wrap.png',
  R07: '/items/R07_Not%20Meth.png',
  R08: '/items/R08_Anus%20Rocket.png',
  R09: '/items/R09_Peanut%27s%20Big%20Brain.png',
  R10: '/items/R10_Morning%20Wood.png',
  R11: '/items/R11_Diarrhea%20Gas.png',
  R12: '/items/R12_Drac%27s%20Gas%20Mask.png',
  R13: '/items/R13_Last%20Resort.png',
  R15: '/items/R15_Silly%20Juice.png',
  R16: '/items/R16_Nutty%20T-Bag.png',
  R17: '/items/R17_NickMerc%20Jerk.png',
} as const;

// --- Rarity Colors ---
// BETA 20.24 — 3-tier ITEM colors (Dylan: "no more white for rarity"). This is
// the map the chest-CHOICE cards (renderChestChoiceScreen via getChestOptionDisplayData)
// AND the collection menu (MenuRenderer) read — common was WHITE text + grey
// border, which is why chest items landed white. Now: common GREEN, rare PURPLE,
// legendary GOLD. uncommon/epic mirror their collapsed tier (never rolled for
// active items, but kept consistent so nothing renders white/red/grey).
export const RARITY_COLORS: Record<ItemRarity, { bg: string; border: string; text: string }> = {
  common:    { bg: '#15240f', border: '#39ff14', text: '#7dff5a' },
  uncommon:  { bg: '#15240f', border: '#39ff14', text: '#7dff5a' },
  rare:      { bg: '#231539', border: '#b15cff', text: '#cf9bff' },
  epic:      { bg: '#231539', border: '#b15cff', text: '#cf9bff' },
  // BETA 20.25 — Dylan: the chest legendary outline (which reads .text) looked
  // "off yellowish white" (#ffe680). Richen to vivid gold #ffd700 to match the
  // level-up legendary border gold he likes.
  legendary: { bg: '#332a0d', border: '#ffd700', text: '#ffd700' },
};

// --- Item Database ---
// BETA 19.1 — 44 active items, 3 stacking buckets. Descriptions show the
// PER-PICKUP value. No "per stack" wording anywhere. Engine multiplies
// statModifier.value × slot.stacks for free-stack items; mechanic items
// have per-item apply paths in itemsAdapter that read slot.stacks.

export const BUNCHA_BULLSHIIII: BullshiiiiItem[] = [
  // =====================================================================
  // COMMON TIER — 17 items
  // Free-stack stat items: maxStacks 99, each pickup adds the flat value.
  // =====================================================================

  {
    id: 'C01',
    name: "Sausage Fingers",
    description: '+15% attack range.',
    flavorText: 'Those fingers have seen things. Terrible, sausage-shaped things.',
    rarity: 'common',
    synergy: 'utility',
    iconUrl: ICON_URLS.C01,
    starterPool: true,
    stackable: true,
    maxStacks: 99,
    statModifiers: [
      // BETA 19.98 — was 'rangeBonus' which has no aggregator in
      // bunchaBullshiiiiSystem. Switched to 'attackRange' (aggregated) + added
      // consumer in itemsAdapter that converts % bump to p.rangeBonus.
      { stat: 'attackRange', value: 15, isPercent: true },
    ],
    effects: [],
    megabonkRef: '',
    hasWeapon: false,
    status: 'active',
  },

  {
    id: 'C02',
    name: "Big Ol' Boy",
    description: '+30 max HP.',
    descriptionTemplate: '{stat}.',   // BETA 21.75 — card text generated from the statModifier (edit the value → card updates)
    flavorText: 'Four patties. Eight slices of cheese. Zero regrets.',
    rarity: 'common',
    synergy: 'health',
    iconUrl: ICON_URLS.C02,
    starterPool: true,
    stackable: true,
    maxStacks: 99,
    statModifiers: [
      { stat: 'maxHp', value: 30, isPercent: false },
    ],
    effects: [],
    megabonkRef: '',
    hasWeapon: false,
    status: 'active',
  },

  {
    id: 'C03',
    name: "Bungulator's Wrench",
    description: '+10% damage.',
    flavorText: 'BUNGULATOR stamped right on the handle. Fixes things. Or breaks them.',
    rarity: 'common',
    synergy: 'damage',
    iconUrl: ICON_URLS.C03,
    starterPool: true,
    stackable: true,
    maxStacks: 99,
    statModifiers: [
      // BETA 19.98 — was 'damageBonus' — aggregator key is 'damage'. Item
      // had been a silent no-op since shipping. Existing consumer at
      // itemsAdapter.ts:1778 handles 'damage' → p.damageBonus.
      { stat: 'damage', value: 10, isPercent: true },
    ],
    effects: [],
    megabonkRef: '',
    hasWeapon: false,
    status: 'active',
  },

  // BETA 19.5 — Lucky Horseshoe SHELVED. Rabbit's Foot is
  // now the sole luck item (no duplicate buffs per Dylan). Sprite file
  // C04 still exists on disk; GOOP GOD'S EYE keeps reusing it as an icon.
  {
    id: 'C04',
    name: "Lucky Horseshoe",
    description: '[SHELVED]',
    flavorText: '',
    rarity: 'common',
    synergy: 'luck',
    iconUrl: ICON_URLS.C04,
    starterPool: false,
    stackable: false,
    maxStacks: 1,
    statModifiers: [],
    effects: [],
    megabonkRef: '',
    hasWeapon: false,
    status: 'shelved',
  },

  {
    id: 'C05',
    name: 'XP Juice',
    description: '+20% XP gain.',
    flavorText: 'Blue liquid. White letters. Tastes like progress.',
    rarity: 'common',
    synergy: 'xp',
    iconUrl: ICON_URLS.C05,
    starterPool: true,
    stackable: true,
    maxStacks: 99,
    statModifiers: [
      { stat: 'xpGain', value: 20, isPercent: true },
    ],
    effects: [],
    megabonkRef: '',
    hasWeapon: false,
    status: 'active',
  },

  {
    id: 'C06',
    name: "Peanut's Underwear",
    description: '+8 armor.',
    flavorText: 'Worn. Stained. Surprisingly protective.',
    rarity: 'common',
    synergy: 'defense',
    iconUrl: ICON_URLS.C06,
    starterPool: true,
    stackable: true,
    maxStacks: 99,
    statModifiers: [
      { stat: 'armor', value: 8, isPercent: false },
    ],
    effects: [],
    megabonkRef: '',
    hasWeapon: false,
    status: 'active',
  },

  {
    id: 'C07',
    name: 'Sticky Fingers',
    description: '+25% pickup range.',
    flavorText: 'Everything sticks to these hands. Everything.',
    rarity: 'common',
    synergy: 'xp',
    iconUrl: ICON_URLS.C07,
    starterPool: true,
    stackable: true,
    maxStacks: 99,
    statModifiers: [
      { stat: 'pickupRange', value: 25, isPercent: true },
    ],
    effects: [],
    megabonkRef: '',
    hasWeapon: false,
    status: 'active',
  },

  {
    id: 'C08',
    name: 'Pre Workout',
    description: '+10% attack speed.',
    flavorText: 'Not FDA approved. Not even close.',
    rarity: 'common',
    synergy: 'damage',
    iconUrl: ICON_URLS.C08,
    starterPool: true,
    stackable: true,
    maxStacks: 99,
    statModifiers: [
      // BETA 19.98 — was 'attackSpeedBonus' — aggregator key is 'attackSpeed'.
      // Item had been a silent no-op since shipping. Existing consumer at
      // itemsAdapter.ts:1782 handles 'attackSpeed' → p.attackSpeedBonus.
      { stat: 'attackSpeed', value: 10, isPercent: true },
    ],
    effects: [],
    megabonkRef: '',
    hasWeapon: false,
    status: 'active',
  },

  {
    id: 'C09',
    name: 'Little Black Dress (TJ Maxx)',
    description: '+12% dodge chance.',
    flavorText: 'Clearance rack. Still fabulous.',
    rarity: 'common',
    synergy: 'utility',
    iconUrl: ICON_URLS.C09,
    starterPool: true,
    stackable: true,
    maxStacks: 99,
    statModifiers: [
      { stat: 'dodgeChance', value: 12, isPercent: true },
    ],
    effects: [],
    megabonkRef: '',
    hasWeapon: false,
    status: 'active',
  },

  {
    id: 'C10',
    name: 'Stim Pod',
    description: '+5 HP regen per second.',
    flavorText: 'Glitter inside. Should NOT be injected into a human.',
    rarity: 'common',
    synergy: 'health',
    iconUrl: ICON_URLS.C10,
    starterPool: true,
    stackable: true,
    maxStacks: 99,
    statModifiers: [
      { stat: 'hpRegen', value: 5, isPercent: false },
    ],
    effects: [],
    megabonkRef: '',
    hasWeapon: false,
    status: 'active',
  },

  // ── Demoted from rare: sprites kept, common-tier roles ───────────────

  // BETA 19.5 — Per Dylan: Rabbit Foot should be LUCK (matches theme +
  // sprite). Was +20% crit damage in 19.0-19.4 — that was my refactor
  // error. Now the SOLE luck item (Lucky Horseshoe shelved this
  // push). Reduced to +15 (was originally +25 in 19.1) because it now
  // stacks freely up to 99 — even 4 picks = +60 luck, plenty for chest
  // rarity bias.
  {
    id: 'R02',
    name: "Rabbit's Foot",
    description: '+50 luck.',
    flavorText: 'Golden and glowing. The rabbit wants it back.',
    rarity: 'legendary',  // BETA 20.11 — gold card (was common)
    synergy: 'luck',
    iconUrl: ICON_URLS.R02,
    starterPool: true,
    stackable: true,
    maxStacks: 99,
    statModifiers: [
      { stat: 'luck', value: 50, isPercent: false },
    ],
    effects: [],
    megabonkRef: '',
    hasWeapon: false,
    status: 'active',
  },

  {
    id: 'R07',
    name: 'Not Meth',
    description: '+15% move speed.',
    flavorText: 'It says NOT METH right on the bag. Totally legit.',
    rarity: 'rare',  // BETA 20.11 — purple card = rare (was common)
    synergy: 'speed',
    iconUrl: ICON_URLS.R07,
    starterPool: true,
    stackable: true,
    maxStacks: 99,
    statModifiers: [
      { stat: 'moveSpeed', value: 15, isPercent: true },
    ],
    effects: [],
    megabonkRef: '',
    hasWeapon: false,
    status: 'active',
  },

  {
    id: 'item_mystery_meat_stew',
    name: 'Mystery Meat Stew',
    description: '+5% damage reduction. Every kill: a tiny random +damage/+HP/+luck roll (diminishing returns).',  // BETA 20.77 — truth-sync: the on-kill roll (the item's REAL power) was invisible on the card
    descriptionTemplate: '{stat}. Every kill: a tiny random +damage/+HP/+luck roll (diminishing returns).',  // BETA 21.77 — {stat} syncs the editable damage-reduction value; extra text preserved
    flavorText: 'It moved. Is it supposed to do that?',
    rarity: 'common',
    synergy: 'defense',
    iconUrl: '/items/item_mystery_meat_stew.png',
    starterPool: true,
    stackable: true,
    maxStacks: 99,
    statModifiers: [
      { stat: 'damageReduction', value: 5, isPercent: true },
    ],
    effects: [],
    megabonkRef: '',
    hasWeapon: false,
    status: 'active',
  },

  {
    id: 'item_write_that_down',
    name: 'Write That Down',
    description: '+15% gold gain.',
    flavorText: 'Luck is taking notes. Always.',
    rarity: 'common',
    synergy: 'luck',
    iconUrl: '/items/item_write_that_down.png',
    starterPool: true,
    stackable: true,
    maxStacks: 99,
    statModifiers: [
      // BETA 19.98 — was 'goldMultiplier' — aggregator has no such key, item
      // was silent no-op. Switched to 'gold' (added to aggregator +
      // itemsAdapter consumer in 19.98).
      { stat: 'gold', value: 15, isPercent: true },
    ],
    effects: [],
    megabonkRef: '',
    hasWeapon: false,
    status: 'active',
  },

  // ── Common mechanic items — capped stack (maxStacks: 5) ───────────────

  {
    id: 'item_caseoh_moms_cooking',
    name: "Mom's Cooking",
    description: 'Heal +5 HP on every kill.',
    // BETA 21.75 — editable; handler reads via tuneOf (engine on-kill heal). Per-stack heal per kill; kill-count taper unchanged.
    tuning: { healPerKill: 5 },
    descriptionTemplate: 'Heal +{healPerKill} HP on every kill.',
    flavorText: 'She made too much. As always.',
    rarity: 'common',
    synergy: 'health',
    iconUrl: '/items/item_caseoh_moms_cooking.png',
    starterPool: true,
    stackable: true,
    maxStacks: 5,
    statModifiers: [],
    effects: [
      { trigger: 'on_kill', description: 'Heal +5 HP on kill', effectId: 'moms_cooking_heal', chance: 1 },
    ],
    megabonkRef: '',
    hasWeapon: false,
    status: 'active',
  },

  {
    id: 'item_voogoo_rager',
    name: 'Rage Juice',
    description: 'Auto-heal 40% max HP when below 50% HP. 45 second cooldown.',
    // BETA 21.75 — editable; handler reads via tuneOf (engine auto-heal tick).
    tuning: { healPctOfMaxHp: 0.4, triggerHpThreshold: 0.5, cooldownSeconds: 45 },
    descriptionTemplate: 'Auto-heal {healPctOfMaxHp:pct}% max HP when below {triggerHpThreshold:pct}% HP. {cooldownSeconds} second cooldown.',
    flavorText: 'Tastes like regret and victory.',
    rarity: 'common',
    synergy: 'health',
    iconUrl: '/items/item_voogoo_rager.png',
    starterPool: true,
    stackable: true,
    maxStacks: 5,
    statModifiers: [],
    effects: [
      { trigger: 'passive', description: 'Auto-heals at low HP', effectId: 'voogoo_rager_active', chance: 1 },
    ],
    megabonkRef: '',
    hasWeapon: false,
    status: 'active',
  },

  // =====================================================================
  // RARE TIER — 17 items, single-mechanic, capped stacking (3-5) where
  // stacking the magnitude makes sense.
  // =====================================================================

  {
    id: 'R01',
    name: "Championship Belt",
    description: '+flat damage equal to 5% of your max HP (stacks up to 3×). The bigger your health bar, the harder you hit.',
    // BETA 21.81 — editable in the Balance Dashboard; handler reads via tuneOf (itemsAdapter passive).
    tuning: { hpDamageFrac: 0.05, refWeaponDamage: 20 },
    descriptionTemplate: '+flat damage equal to {hpDamageFrac:pct}% of your max HP per copy. The bigger your health bar, the harder you hit.',
    flavorText: 'The bigger you are, the harder you hit.',
    rarity: 'rare',
    synergy: 'damage',
    iconUrl: ICON_URLS.R01,
    starterPool: false,
    stackable: true,
    maxStacks: 3,
    statModifiers: [],
    effects: [
      { trigger: 'passive', description: 'Gain bonus flat damage equal to 5% of max HP', effectId: 'championship_belt_hp_damage', chance: 1 },
    ],
    megabonkRef: '',
    hasWeapon: false,
    status: 'active',
    unlockCondition: { type: 'total_damage_taken', value: 300, description: 'Take 300 total damage' },
  },

  {
    id: 'R03',
    name: 'The Wyfe Beater',
    description: 'When hit, deal thorns damage equal to 50% of your armor (max 50 per copy).',
    // BETA 21.81 — editable in the Balance Dashboard; handler reads via tuneOf (itemsAdapter passive adds to p.thorns).
    tuning: { thornsArmorFrac: 0.5, thornsCapPerStack: 50 },
    descriptionTemplate: 'When hit, deal thorns damage equal to {thornsArmorFrac:pct}% of your armor (max {thornsCapPerStack} per copy).',
    flavorText: 'A cast iron frying pan. Covered in spikes. This is not a cooking utensil.',
    rarity: 'rare',
    synergy: 'defense',
    iconUrl: ICON_URLS.R03,
    starterPool: false,
    stackable: true,
    maxStacks: 3,
    statModifiers: [],
    effects: [
      { trigger: 'on_damage', description: 'Reflect thorns damage equal to 50% of armor', effectId: 'wyfe_beater_thorns', chance: 1 },
    ],
    megabonkRef: '',
    hasWeapon: false,
    status: 'active',
    unlockCondition: { type: 'total_damage_taken', value: 500, description: 'Take 500 total damage' },
  },

  {
    id: 'R05',
    name: "Peanut's Blood Bag",
    description: 'Heal for 3% of damage dealt on hit.',
    // BETA 21.79 — editable in the Balance Dashboard; handler reads via tuneOf
    // (itemsAdapter aggregate adds lifestealPctPerStack × copies to player lifesteal).
    tuning: { lifestealPctPerStack: 3 },
    descriptionTemplate: 'Heal for {lifestealPctPerStack}% of damage dealt on hit (per copy).',
    flavorText: 'Glowing red. Dripping. Probably someone else\'s.',
    rarity: 'rare',
    synergy: 'health',
    iconUrl: ICON_URLS.R05,
    starterPool: false,
    stackable: true,
    maxStacks: 3,
    statModifiers: [],
    effects: [
      { trigger: 'on_hit', description: 'Heal for 3% of damage dealt', effectId: 'blood_bag_lifesteal', chance: 1 },
    ],
    megabonkRef: '',
    hasWeapon: false,
    status: 'active',
    unlockCondition: { type: 'total_kills', value: 150, description: 'Kill 150 enemies' },
  },

  {
    id: 'R06',
    name: "Bubble Wrap",
    description: 'Reflect 30% of damage taken back at the attacker.',
    // BETA 21.81 — editable in the Balance Dashboard; handlers read via tuneOf
    // (enemyAdapter rolls the invuln proc; itemEffectSystem carries the reflect damage).
    tuning: { reflectFracPerStack: 0.30, invulnChance: 0.10, bubbleInvulnSeconds: 1.0 },
    descriptionTemplate: 'Reflect {reflectFracPerStack:pct}% of damage taken back at the attacker (per copy). {invulnChance:pct}% chance the hit grants brief invulnerability.',
    flavorText: 'She is NOT happy but she IS protected.',
    rarity: 'rare',
    synergy: 'defense',
    iconUrl: ICON_URLS.R06,
    starterPool: false,
    stackable: true,
    maxStacks: 2,
    statModifiers: [],
    effects: [
      { trigger: 'on_damage', description: 'Reflect 30% of damage taken', effectId: 'bubble_wrap_reflect', chance: 1 },
    ],
    megabonkRef: '',
    hasWeapon: false,
    status: 'active',
    unlockCondition: { type: 'total_deaths', value: 3, description: 'Die 3 times' },
  },

  {
    id: 'R08',
    name: 'Anus Rocket',
    description: '15% chance on kill to launch a homing rocket.',
    // BETA 21.79 — editable in the Balance Dashboard; handlers read via tuneOf
    // (weaponAdapter on-kill proc + damage; itemEffectSystem rocket blast/life).
    // procDecay is the geometric base: chance = 1 - procDecay^cbrt(stacks)
    // (0.85 → 15% at 1 stack). dmgMult: rocket dmg = hit dmg × this × stacks.
    tuning: { procDecay: 0.85, dmgMult: 1.5, blastBase: 80, blastPerSqrtStack: 20, rocketLifetime: 6, selfDestructDmgFrac: 0.35 },
    descriptionTemplate: 'On kill, chance to launch a homing rocket that blows up nearby enemies. More copies = higher chance and bigger blast.',
    flavorText: 'It comes from where you think it comes from.',
    rarity: 'legendary',  // BETA 20.11 — gold card (was rare)
    synergy: 'damage',
    iconUrl: ICON_URLS.R08,
    starterPool: false,
    stackable: true,
    maxStacks: 5,
    statModifiers: [],
    effects: [
      { trigger: 'on_kill', description: 'Launch a homing rocket on kill', effectId: 'anus_rocket_missile', chance: 0.15 },
    ],
    megabonkRef: '',
    hasWeapon: true,
    status: 'active',
    unlockCondition: { type: 'total_kills', value: 300, description: 'Kill 300 enemies' },
  },

  {
    id: 'R11',
    name: 'Diarrhea Gas',
    description: 'Release a poison cloud when you take damage.',
    // BETA 21.81 — editable in the Balance Dashboard; spawnPoisonCloud reads via tuneOf.
    // cloudRadius = cloudRadiusBase + copies*cloudRadiusPerStack; dps = cloudDpsPerStack*copies
    // (both ×player damage/area). Lifetime + tick from cloudLifetime / cloudTickInterval.
    tuning: { cloudLifetime: 3.0, cloudRadiusBase: 80, cloudRadiusPerStack: 20, cloudDpsPerStack: 8, cloudTickInterval: 0.25 },
    descriptionTemplate: 'Release a poison cloud when you take damage ({cloudDpsPerStack} dmg/s per copy over {cloudLifetime}s).',
    flavorText: 'The toilet looks personally offended.',
    rarity: 'rare',
    synergy: 'defense',
    iconUrl: ICON_URLS.R11,
    starterPool: false,
    stackable: true,
    maxStacks: 5,
    statModifiers: [],
    effects: [
      { trigger: 'on_damage', description: 'Release a poison cloud on damage', effectId: 'diarrhea_gas_poison_cloud', chance: 1 },
    ],
    megabonkRef: '',
    hasWeapon: false,
    status: 'active',
    unlockCondition: { type: 'total_damage_taken', value: 200, description: 'Take 200 total damage' },
  },

  {
    id: 'R12',
    name: "Gas Helmet",
    description: 'Gain overheal (up to 20% max HP) when hitting poisoned enemies.',
    // BETA 21.81 — editable in the Balance Dashboard; weaponAdapter on-hit + itemsAdapter
    // shield-floor read via tuneOf. Each poisoned hit grants overhealPerStack*copies shield,
    // capped at overhealCapPct of max HP.
    tuning: { overhealPerStack: 3, overhealCapPct: 0.2 },
    descriptionTemplate: 'Gain overheal (up to {overhealCapPct:pct}% max HP) when hitting poisoned enemies.',
    flavorText: 'Dark hair. Glowing red eyes. He looks absolutely menacing.',
    rarity: 'rare',
    synergy: 'health',
    iconUrl: ICON_URLS.R12,
    starterPool: false,
    stackable: true,
    maxStacks: 2,
    statModifiers: [],
    effects: [
      { trigger: 'on_hit', description: 'Overheal on hitting poisoned enemies', effectId: 'gas_mask_overheal_poison', chance: 1 },
    ],
    megabonkRef: '',
    hasWeapon: false,
    status: 'active',
    unlockCondition: { type: 'wave_reached', value: 7, description: 'Reach wave 7 in a run' },
  },

  {
    id: 'R13',
    name: 'Last Resort',
    description: '+30% damage when below 25% HP.',
    // BETA 21.81 — editable in the Balance Dashboard; recalculateStats reads via tuneOf.
    // While HP fraction is below hpThreshold, adds lowHpDmgBonus damage% per copy.
    tuning: { lowHpDmgBonus: 30, hpThreshold: 0.25 },
    descriptionTemplate: '+{lowHpDmgBonus}% damage when below {hpThreshold:pct}% HP.',
    flavorText: 'Phone at 1%. You at 1%. Time to go all in.',
    rarity: 'rare',
    synergy: 'damage',
    iconUrl: ICON_URLS.R13,
    starterPool: false,
    stackable: true,
    maxStacks: 2,
    statModifiers: [],
    effects: [
      { trigger: 'passive', description: '+30% damage when below 25% HP', effectId: 'last_resort_low_hp_damage', chance: 1 },
    ],
    megabonkRef: '',
    hasWeapon: false,
    status: 'active',
    unlockCondition: { type: 'total_deaths', value: 7, description: 'Die 7 times' },
  },

  {
    id: 'R16',
    name: 'Nutty T-Bag',
    description: 'A loyal T-Bag orbits you and dive-bombs nearby enemies (20 dmg per copy; dives faster with more copies).',
    // BETA 21.79 — editable in the Balance Dashboard; handlers read via tuneOf
    // (engine sets dmg = dmgPerStack × copies; itemEffectSystem handles the dive
    // cooldown = max(dashCdFloor, dashCdBase / copies) and the blast radius).
    tuning: { dmgPerStack: 20, dashCdBase: 2.5, dashCdFloor: 0.8, blastBase: 55, blastPerStack: 15 },
    descriptionTemplate: 'A loyal T-Bag orbits you and dive-bombs nearby enemies ({dmgPerStack} dmg per copy; dives faster with more copies).',
    flavorText: 'Peanut bottom. String. Teabag. Art.',
    rarity: 'rare',
    synergy: 'utility',
    iconUrl: ICON_URLS.R16,
    starterPool: false,
    stackable: true,
    maxStacks: 5,
    statModifiers: [],
    effects: [
      { trigger: 'on_move', description: 'Summon seeking T-Bag every 250 units moved', effectId: 'nutty_tbag_summon', chance: 1 },
    ],
    megabonkRef: '',
    hasWeapon: false,
    status: 'active',
    unlockCondition: { type: 'total_kills', value: 500, description: 'Kill 500 enemies' },
  },

  // BETA 22.49 — 'Cursed Chat' (R18) item REMOVED with the live-chat feature.

  {
    id: 'item_stay_frosty',
    name: 'Stay Frosty',
    description: 'Player projectiles apply a 1.5s slow (50%) on hit.',
    // BETA 21.75 — editable; handlers read via tuneOf (enemySystem on-hit).
    tuning: { slowDuration: 1.5, slowDurationPerStack: 0.5, slowMultiplier: 0.5 },
    descriptionTemplate: 'Player projectiles apply a {slowDuration}s slow ({slowMultiplier:slowpct}% slower) on hit.',
    flavorText: 'Stay frosty. Stay alive.',
    rarity: 'common',  // BETA 20.11 — green card = common (was rare)
    synergy: 'utility',
    iconUrl: '/items/item_stay_frosty.png',
    starterPool: false,
    stackable: true,
    maxStacks: 3,
    statModifiers: [],
    effects: [
      { trigger: 'on_hit', description: 'Apply 1.5s slow (50%) on hit', effectId: 'stay_frosty_slow', chance: 1 },
    ],
    megabonkRef: '',
    hasWeapon: false,
    status: 'active',
  },

  {
    id: 'item_big_mac_special_sauce',
    name: 'Big Mac Special Sauce',
    description: '25% chance on hit to apply a 4s burn DoT (8 dmg/s).',
    // BETA 21.75 — editable in the Balance Dashboard; handlers read these via tuneOf
    // (enemySystem on-hit). Fractions stay fractions (procChance 0.25 = 25%).
    tuning: { procChance: 0.25, luckChanceCap: 0.5, burnDuration: 4, burnDurationPerStack: 1.5, burnDps: 8, eliteBossMult: 0.5 },
    descriptionTemplate: '{procChance:pct}% chance on hit to apply a {burnDuration}s burn DoT ({burnDps} dmg/s).',
    flavorText: 'Special sauce. Special situation.',
    rarity: 'common',  // BETA 20.11 — green card = common (was rare)
    synergy: 'damage',
    iconUrl: '/items/item_big_mac_special_sauce.png',
    starterPool: false,
    stackable: true,
    maxStacks: 3,
    statModifiers: [],
    effects: [
      { trigger: 'on_hit', description: '25% chance: apply 4s burn DoT (8 dmg/s)', effectId: 'big_mac_burn', chance: 0.25 },
    ],
    megabonkRef: '',
    hasWeapon: false,
    status: 'active',
  },

  {
    id: 'item_dracs_gas_mask',
    name: "Gas Mask",
    description: "Pairs with Vampire Fang. When both equipped, lifesteal increases from 8% to 20%.",
    // BETA 21.81 — editable in the Balance Dashboard; weaponSystem reads via tuneOf.
    // When Vampire Fang is equipped with this mask, its per-hit lifesteal is raised to
    // at least boostedLifestealPct. (The 8% base lives on the Vampire Fang weapon def.)
    tuning: { boostedLifestealPct: 0.2 },
    descriptionTemplate: "Pairs with Vampire Fang. When both equipped, lifesteal increases to {boostedLifestealPct:pct}%.",
    flavorText: "The mask doesn't breathe. It feeds.",
    rarity: 'rare',
    synergy: 'health',
    iconUrl: '/items/item_dracs_gas_mask.png',
    starterPool: false,
    stackable: false,
    maxStacks: 1,
    statModifiers: [],
    effects: [
      { trigger: 'passive', description: "Vampire Fang lifesteal 8% → 20% when both equipped", effectId: 'dracs_gas_mask_synergy', chance: 1 },
    ],
    megabonkRef: '',
    hasWeapon: false,
    status: 'active',
  },

  {
    id: 'item_timmy_repellent_spray',
    name: 'Bully Spray',
    description: '-50% damage taken from Brawlers. Brawlers within 400wu move 35% slower.',
    // BETA 21.79 — editable; handlers read via tuneOf (enemyAdapter dmg-halving + enemySystem nearby-slow).
    tuning: { damageReductionPct: 0.5, slowRadius: 400, slowMultiplier: 0.65 },
    descriptionTemplate: 'Take {damageReductionPct:pct}% less damage from Brawlers. Brawlers within {slowRadius} move {slowMultiplier:slowpct}% slower.',
    flavorText: "Apply liberally. He still won't leave.",
    rarity: 'common',  // BETA 20.11 — green card = common (was rare)
    synergy: 'defense',
    iconUrl: '/items/item_timmy_repellent_spray.png',
    starterPool: false,
    stackable: false,
    maxStacks: 1,
    statModifiers: [],
    effects: [
      { trigger: 'on_damage', description: '-50% damage from Brawlers + slow Brawlers nearby', effectId: 'timmy_repellent', chance: 1 },
    ],
    megabonkRef: '',
    hasWeapon: false,
    status: 'active',
  },

  {
    id: 'item_asmons_desk_banana',
    name: "Desk Banana",
    description: 'LEGENDARY. Hurls a banana that LURES nearby enemies in, then crit-blasts the whole group. Loot auto-collects to you. Projectile-count buffs throw more.',
    // BETA 21.75 — editable; handler reads via tuneOf (engine auto-drop tick, engine.ts ~4590-4677).
    tuning: { hitDamage: 35, blastRadius: 36, dropCooldownSec: 8.0, minCooldownSec: 2.0, trapLifeSec: 30.0, enemySlowMult: 1.0, aoeRadiusBonus: 30, popFadeSec: 0.4, lootGrabBonus: 50 },
    descriptionTemplate: 'Throw a banana. When a bad guy touches it, everyone nearby gets crit-blasted for {hitDamage} damage. Throws again every {dropCooldownSec} seconds.',
    flavorText: "It's been on his desk for 6 months. It's only gotten stronger.",
    rarity: 'legendary',
    synergy: 'utility',
    iconUrl: '/items/item_asmons_desk_banana.png',
    starterPool: true,   // BETA 21.71 — was an unreachable legendary (no starterPool, no unlock quest). Now in the pool so the legendary tier shows variety.
    stackable: true,
    maxStacks: 5,
    statModifiers: [],
    effects: [
      { trigger: 'passive', description: 'Auto-drops banana traps; ~8s cooldown', effectId: 'asmons_banana_active', chance: 1 },
    ],
    megabonkRef: '',
    hasWeapon: false,
    status: 'active',
  },

  {
    id: 'item_whoopie_cushion_of_doom',
    name: 'Whoopie Cushion of Doom',
    description: 'LEGENDARY. Hurls a cushion that LURES nearby enemies in, then AoE-FREEZES the whole group for 2s + damage. Loot auto-collects to you. Projectile-count buffs throw more.',
    // BETA 21.75 — editable; handler reads via tuneOf (engine auto-drop tick, engine.ts ~4494-4583).
    tuning: { freezeSeconds: 2.0, hitDamage: 25, blastRadius: 32, dropCooldownSec: 6.0, minCooldownSec: 2.0, trapLifeSec: 30.0, enemySlowMult: 0.4, aoeRadiusBonus: 30, popFadeSec: 0.6, lootGrabBonus: 50 },
    descriptionTemplate: 'Throw a cushion. When a bad guy touches it, everyone nearby freezes for {freezeSeconds} seconds and takes {hitDamage} damage. Throws again every {dropCooldownSec} seconds.',
    flavorText: 'The gas is real. The stun is realer.',
    rarity: 'legendary',
    synergy: 'chaos',
    iconUrl: '/items/item_whoopie_cushion_of_doom.png',
    starterPool: true,   // BETA 21.71 — was an unreachable legendary (no starterPool, no unlock quest). Now in the pool so the legendary tier shows variety.
    stackable: true,
    maxStacks: 5,
    statModifiers: [],
    effects: [
      { trigger: 'passive', description: 'Auto-drops stun cushions; ~6s cooldown', effectId: 'whoopie_cushion_active', chance: 1 },
    ],
    megabonkRef: '',
    hasWeapon: false,
    status: 'active',
  },

  {
    id: 'item_shit',
    name: 'SHIT',
    description: 'Player leaves a trail of sticky poop. Enemies who walk through it are slowed.',
    // BETA 21.75 — editable; handler reads via tuneOf (engine SHIT-trail drop). puddleSlowMult is a speed-mult (0.5 = 50% slower).
    tuning: { puddleSlowMult: 0.5, dropInterval: 1.5, puddleBaseRadius: 30, puddleRadiusPerStack: 4, puddleLifetime: 8.0 },
    descriptionTemplate: 'Player leaves a trail of sticky poop. Enemies who walk through it are {puddleSlowMult:slowpct}% slower.',
    flavorText: 'Happens to the best of us.',
    rarity: 'common',  // BETA 20.11 — green card = common (was rare)
    synergy: 'utility',
    iconUrl: '/items/item_poop_deck.png',
    starterPool: false,
    stackable: true,
    maxStacks: 5,
    statModifiers: [],
    effects: [
      { trigger: 'on_move', description: 'Drops a slow puddle behind the player (50% slow, 3s)', effectId: 'shit_trail', chance: 1 },
    ],
    megabonkRef: '',
    hasWeapon: false,
    status: 'active',
  },

  {
    id: 'UC03',
    name: 'Adrenaline Pod',
    description: '+12% attack speed for 4s after taking damage.',
    // BETA 21.79 — editable; handlers read via tuneOf (enemyAdapter sets timer on hit, itemsAdapter applies the buff).
    tuning: { attackSpeedPct: 12, durationSeconds: 4 },
    descriptionTemplate: '+{attackSpeedPct}% attack speed for {durationSeconds}s after you get hit (per copy).',
    flavorText: "Push the button. Feel everything. Twice.",
    // BETA 20.11 — 3-tier rarity collapse (green bg = common). The 'uncommon'
    // tier is retired; this green-card item is now common. Effect unchanged.
    rarity: 'common',
    synergy: 'damage',
    iconUrl: ICON_URLS.UC03,
    starterPool: false,
    stackable: true,
    maxStacks: 5,
    statModifiers: [],
    effects: [
      { trigger: 'on_damage', description: '+12% attack speed for 4s', effectId: 'adrenaline_reactive', chance: 1 },
    ],
    megabonkRef: '',
    hasWeapon: false,
    status: 'active',
  },

  // =====================================================================
  // EPIC TIER — 6 items, build-defining mechanics.
  // =====================================================================

  {
    id: 'R09',
    name: "Peanut's Big Brain",
    description: 'XP orbs become hazards — enemies that walk into them take damage.',
    // BETA 21.81 — editable in the Balance Dashboard; handler reads via tuneOf (engine XP-orb hazard tick).
    tuning: { dmgPerStack: 1, tickSeconds: 0.45, touchRange: 16 },
    descriptionTemplate: 'XP orbs become hazards — enemies that walk into them take {dmgPerStack} damage about every {tickSeconds}s.',
    flavorText: 'That brain knows exactly what it\'s doing.',
    rarity: 'rare',  // BETA 20.11 — purple card = rare (was epic; epic tier retired)
    synergy: 'xp',
    iconUrl: ICON_URLS.R09,
    starterPool: false,
    stackable: false,  // BETA 20.62 — Dylan: one-and-done — removed from the offer pool once owned
    maxStacks: 1,      // BETA 20.62 — was 3 (no longer stacks / re-offers)
    statModifiers: [],
    effects: [
      { trigger: 'passive', description: 'XP orbs on the ground damage enemies that touch them', effectId: 'big_brain_xp_damage', chance: 1 },
    ],
    megabonkRef: '',
    hasWeapon: false,
    status: 'active',
    unlockCondition: { type: 'level_reached', value: 8, description: 'Reach level 8 in a run' },
  },

  {
    id: 'R10',
    name: 'Morning Wood',
    description: 'Thorns damage has a 25% chance to explode for AoE damage.',
    // BETA 21.81 — editable in the Balance Dashboard; handlers read via tuneOf
    // (enemyAdapter rolls the proc; itemEffectSystem sizes the blast + damage).
    tuning: { explodeChance: 0.25, dmgMultPerStack: 2.0, radiusBase: 90, radiusPerStack: 22 },
    descriptionTemplate: 'Thorns damage has a {explodeChance:pct}% chance to explode for {dmgMultPerStack}× thorns AoE damage (per copy).',
    flavorText: 'A thick log. A warm sunrise. Majestic.',
    rarity: 'rare',  // BETA 20.11 — purple card = rare (was epic; epic tier retired)
    synergy: 'defense',
    iconUrl: ICON_URLS.R10,
    starterPool: false,
    stackable: true,
    maxStacks: 3,
    statModifiers: [],
    effects: [
      { trigger: 'on_damage', description: 'Thorns damage explodes for AoE (25% chance)', effectId: 'morning_wood_thorns_explode', chance: 0.25 },
    ],
    megabonkRef: '',
    hasWeapon: false,
    status: 'active',
    unlockCondition: { type: 'total_damage_taken', value: 750, description: 'Take 750 total damage' },
  },

  {
    id: 'R15',
    name: 'Silly Juice',
    description: 'Kills build damage: +5% × √kills (25 kills ≈ +25%). More copies raise the cap. Resets on taking damage.',
    // BETA 21.77 — editable; handlers read via tuneOf (itemsAdapter kill-stack + damage apply).
    tuning: { pctPerSqrtKill: 5, capPerCopy: 50 },
    descriptionTemplate: 'Killing enemies stacks up your damage (the more you kill, the stronger you hit, up to a limit). Taking a hit resets it. Each copy raises the limit by {capPerCopy} kills.',
    flavorText: 'Simple brown bottle. SILLY JUICE on the label. No questions asked.',
    rarity: 'rare',  // BETA 20.11 — purple card = rare (was epic; epic tier retired)
    synergy: 'damage',
    iconUrl: ICON_URLS.R15,
    starterPool: false,
    stackable: true,
    maxStacks: 3,
    statModifiers: [],
    effects: [
      { trigger: 'on_kill', description: '+5% damage per kill, stacks up to 25 times, resets on damage', effectId: 'silly_juice_kill_stacks', chance: 1 },
    ],
    megabonkRef: '',
    hasWeapon: false,
    status: 'active',
    unlockCondition: { type: 'total_kills', value: 1000, description: 'Kill 1,000 enemies' },
  },

  {
    id: 'R17',
    name: 'Beef Jerky',
    description: 'Critical hits explode for 50% of damage as AoE splash.',
    // BETA 21.78 — editable; handler reads via tuneOf (itemEffectSystem crit explosion).
    // Splash = splashPct × copies, so 2 copies = 100% of the crit's damage.
    tuning: { splashPct: 0.5, radiusBase: 100, radiusPerStack: 25 },
    descriptionTemplate: 'Critical hits explode for {splashPct:pct}% of damage per copy as AoE splash.',
    flavorText: 'This bag hits different.',
    rarity: 'rare',  // BETA 20.11 — purple card = rare (was epic; epic tier retired)
    synergy: 'damage',
    iconUrl: ICON_URLS.R17,
    starterPool: false,
    stackable: true,
    maxStacks: 5,
    statModifiers: [],
    effects: [
      { trigger: 'on_crit', description: 'Crit explodes for 50% AoE splash', effectId: 'nickmerc_jerk_crit_splash', chance: 1 },
    ],
    megabonkRef: '',
    hasWeapon: false,
    status: 'active',
    unlockCondition: { type: 'total_kills', value: 400, description: 'Kill 400 enemies' },
  },

  {
    id: 'item_bungulators',
    name: 'Bungulators',
    description: 'Spawns a melee brawler minion. Punches nearby enemies.',
    // BETA 21.75 — editable; handler reads via tuneOf (engine bungulator-minion punch). punchDamage scales with damage%/crit; attackRange/cooldown scale with area%/attack-speed%.
    tuning: { punchDamage: 18, attackRange: 60, punchCooldown: 0.7 },
    descriptionTemplate: 'Spawns a melee brawler minion. Punches nearby enemies for {punchDamage}.',
    flavorText: "He doesn't ask questions. He just hits.",
    rarity: 'legendary',  // BETA 20.11 — gold card (was epic)
    synergy: 'damage',
    iconUrl: '/items/item_bungulators.png',
    starterPool: true,   // BETA 21.71 — was an unreachable legendary (no starterPool, no unlock quest). Now in the pool so the legendary tier shows variety.
    stackable: true,
    maxStacks: 3,
    statModifiers: [],
    effects: [
      { trigger: 'passive', description: 'Spawns a melee brawler minion', effectId: 'bungulators_minion', chance: 1 },
    ],
    megabonkRef: '',
    hasWeapon: false,
    status: 'active',
  },

  {
    id: 'item_iranian_bird',
    name: 'Iranian Bird',
    description: 'A scrap-built drone orbits you and auto-fires at enemies.',
    // BETA 21.75 — editable; handler reads via tuneOf (engine iranian-bird drone fire). shotDamage scales with damage%/crit; scanRange scales with range%; orbitSpeed is the rad/s spin.
    tuning: { shotDamage: 14, scanRange: 300, fireCooldown: 0.6, orbitSpeed: 1.6, idleRescan: 0.15 },
    descriptionTemplate: 'A scrap-built drone orbits you and auto-fires at enemies for {shotDamage}.',
    flavorText: 'Built in a cave. With a box of scraps.',
    rarity: 'legendary',  // BETA 20.11 — gold card (was epic)
    synergy: 'damage',
    iconUrl: '/items/item_iranian_bird.png',
    starterPool: true,   // BETA 21.71 — was an unreachable legendary (no starterPool, no unlock quest). Now in the pool so the legendary tier shows variety.
    stackable: false,
    maxStacks: 1,
    statModifiers: [],
    effects: [
      { trigger: 'passive', description: 'Spawns an orbiting auto-firing drone companion', effectId: 'iranian_bird_minion', chance: 1 },
    ],
    megabonkRef: '',
    hasWeapon: false,
    status: 'active',
  },

  // =====================================================================
  // LEGENDARY TIER — 4 items, run-defining payoffs. Mostly UNIQUE (single
  // pickup) because the effect is a binary ability, not a stat boost.
  // =====================================================================

  {
    id: 'item_sheeesh',
    name: 'Sheeesh',
    description: 'One-shot revive. When you would die, survive at 25% HP instead.',
    // BETA 21.79 — editable; handler reads via tuneOf (engine death-intercept revive).
    tuning: { reviveHpPct: 0.25, invulnSeconds: 2 },
    descriptionTemplate: 'When you would die, live with {reviveHpPct:pct}% HP instead (once per copy). {invulnSeconds}s of being unhittable after.',
    flavorText: "The water's fine.",
    rarity: 'legendary',
    synergy: 'defense',
    iconUrl: '/items/item_sheeesh.png',
    starterPool: true,   // BETA 21.71 — was an unreachable legendary (no starterPool, no unlock quest). Now in the pool so the legendary tier shows variety.
    stackable: false,
    maxStacks: 1,
    statModifiers: [],
    effects: [
      { trigger: 'on_damage', description: 'Survive lethal damage at 25% HP, one use per run', effectId: 'sheeesh_revive', chance: 1 },
    ],
    megabonkRef: '',
    hasWeapon: false,
    status: 'active',
  },

  {
    id: 'item_liar_deceiver_false_prophet',
    name: 'Liar Deceiver False Prophet',
    description: 'When you would die, the decoy you leave behind DETONATES — a huge nuke that kills everything around you (not mega bosses) + warps you to safety with 5s invuln.',
    // BETA 21.79 / 22.06 — editable; handler reads via tuneOf (engine death-intercept decoy NUKE).
    // blastDamage/blastRadius = the death-save explosion (kills anything it touches in radius;
    // mega/final bosses take it but can't be KILLED by it, so you can't suicide-cheese a boss).
    tuning: { invulnSeconds: 5, decoyDistance: 80, blastDamage: 5000, blastRadius: 520 },
    descriptionTemplate: 'When you would die, leave a fake you behind that EXPLODES — a {blastDamage}-damage nuke in a {blastRadius} radius that kills everything it touches (mega bosses survive). You warp {decoyDistance} away with 1 HP + {invulnSeconds}s invuln. Once per run.',
    flavorText: 'Was that you? Are you sure?',
    rarity: 'legendary',
    synergy: 'defense',
    iconUrl: '/items/item_liar_deceiver_false_prophet.png',
    starterPool: true,   // BETA 21.71 — was an unreachable legendary (no starterPool, no unlock quest). Now in the pool so the legendary tier shows variety.
    stackable: false,
    maxStacks: 1,
    statModifiers: [],
    effects: [
      { trigger: 'on_damage', description: 'One-shot decoy revive at 5s invuln', effectId: 'liar_decoy_revive', chance: 1 },
    ],
    megabonkRef: '',
    hasWeapon: false,
    status: 'active',
  },

  {
    id: 'L03',
    name: "GOOP GOD'S EYE",
    description: '+25% crit chance and +100% crit damage.',
    flavorText: 'You see the weak spots. On EVERYTHING. Even reality.',
    rarity: 'legendary',
    synergy: 'damage',
    iconUrl: ICON_URLS.C04,
    starterPool: false,
    stackable: true,
    maxStacks: 2,
    statModifiers: [
      // BETA 21.41 — critChance base is 1, so isPercent multiplied to 1.25 = +0.25 points
      // (~100x lie vs the "+25%" card). Flat → +25 points, matching every other crit source.
      { stat: 'critChance', value: 25, isPercent: false },
      { stat: 'critDamage', value: 100, isPercent: true },  // critDamage sums additively — already correct
    ],
    effects: [],
    megabonkRef: '',
    hasWeapon: false,
    status: 'active',
    unlockCondition: { type: 'total_kills', value: 5000, description: 'Kill 5,000 enemies total' },
  },

  {
    id: 'item_bungula_chambers',
    name: "Bungulator's Extra Chambers",
    description: '+3 extra projectiles on all weapons. Synergy: +50% damage when Bungulators minion equipped.',
    // BETA 21.79 — editable in the Balance Dashboard; handler reads via tuneOf
    // (itemsAdapter aggregate). extraProjPerStack × copies = bonus projectiles on ALL
    // weapons; synergyDmgPct is a flat damage% when the Bungulators minion is also equipped.
    tuning: { extraProjPerStack: 3, synergyDmgPct: 50 },
    descriptionTemplate: '+{extraProjPerStack} extra projectiles on all weapons. Synergy: +{synergyDmgPct}% damage when Bungulators minion equipped.',
    flavorText: 'BUNGULATOR. Stamped on every chamber.',
    rarity: 'legendary',
    synergy: 'damage',
    // BETA 20.11 — was the deleted N05 Bungulazine perc (borrowed perc_bungulazine.png).
    // Now its own legendary Buncha Bullshit item w/ gold "5 fists" card.
    iconUrl: '/items/item_bungula_chambers.png',
    starterPool: false,
    stackable: false,   // BETA 21.58 — Dylan: "+3 more projectiles is extreme." BUG: card says "+3 extra projectiles" but +3/stack × maxStacks 3 = +9 on ALL weapons. Capped to ONE copy = +3, matching its own description.
    maxStacks: 1,
    // BETA 19.98 — statModifier was dead (no aggregator for projectileCountBonus).
    // Removed dead modifier; the real apply path is the special handler in
    // itemsAdapter.ts (was using sqrt — fixed to +3 per stack per the
    // description).
    statModifiers: [],
    effects: [],
    megabonkRef: '',
    hasWeapon: false,
    status: 'active',
    unlockCondition: { type: 'wave_reached', value: 8, description: 'Reach wave 8 in a single run' },
  },

  // =====================================================================
  // SHELVED — tier-bloat duplicates (sprite reuses of base items). The
  // role they used to play (bigger numbers on the same theme) is now
  // covered by free-stacking the base item: pick up Big Ol' Boy 3 times,
  // get +90 HP, no need for a separate Big Ol' Boy III.
  // =====================================================================

  {
    id: 'UC04', name: "Big Ol' Boy II", description: '[SHELVED]', flavorText: '',
    rarity: 'uncommon', synergy: 'health', iconUrl: ICON_URLS.C02,
    starterPool: false, stackable: false, maxStacks: 1,
    statModifiers: [], effects: [], megabonkRef: '', hasWeapon: false, status: 'shelved',
  },
  {
    id: 'UC06', name: "Peanut's Thick Underwear", description: '[SHELVED]', flavorText: '',
    rarity: 'uncommon', synergy: 'defense', iconUrl: ICON_URLS.C06,
    starterPool: false, stackable: false, maxStacks: 1,
    statModifiers: [], effects: [], megabonkRef: '', hasWeapon: false, status: 'shelved',
  },
  {
    id: 'E01', name: "Bungulator's Wrench III", description: '[SHELVED]', flavorText: '',
    rarity: 'epic', synergy: 'damage', iconUrl: ICON_URLS.C03,
    starterPool: false, stackable: false, maxStacks: 1,
    statModifiers: [], effects: [], megabonkRef: '', hasWeapon: false, status: 'shelved',
  },
  {
    id: 'E02', name: 'Pre Workout III', description: '[SHELVED]', flavorText: '',
    rarity: 'epic', synergy: 'luck', iconUrl: ICON_URLS.C08,
    starterPool: false, stackable: false, maxStacks: 1,
    statModifiers: [], effects: [], megabonkRef: '', hasWeapon: false, status: 'shelved',
  },
  {
    id: 'E03', name: "Big Ol' Boy III", description: '[SHELVED]', flavorText: '',
    rarity: 'epic', synergy: 'health', iconUrl: ICON_URLS.C02,
    starterPool: false, stackable: false, maxStacks: 1,
    statModifiers: [], effects: [], megabonkRef: '', hasWeapon: false, status: 'shelved',
  },
  {
    id: 'E04', name: "Lucky Horseshoe III", description: '[SHELVED]', flavorText: '',
    rarity: 'epic', synergy: 'luck', iconUrl: ICON_URLS.C04,
    starterPool: false, stackable: false, maxStacks: 1,
    statModifiers: [], effects: [], megabonkRef: '', hasWeapon: false, status: 'shelved',
  },
  {
    id: 'L01', name: 'BUNGULATOR PRIME', description: '[SHELVED]', flavorText: '',
    rarity: 'legendary', synergy: 'damage', iconUrl: ICON_URLS.C03,
    starterPool: false, stackable: false, maxStacks: 1,
    statModifiers: [], effects: [], megabonkRef: '', hasWeapon: false, status: 'shelved',
  },
  {
    id: 'L02', name: "Peanut's Final Form", description: '[SHELVED]', flavorText: '',
    rarity: 'legendary', synergy: 'health', iconUrl: ICON_URLS.C06,
    starterPool: false, stackable: false, maxStacks: 1,
    statModifiers: [], effects: [], megabonkRef: '', hasWeapon: false, status: 'shelved',
  },
];

// ── BETA 22.89 — WILD CARDS TAKEOVER (Dylan: "remove the old buncha bullshit
// items. delete them. they are no use to me anymore.")
// BETA 22.90 — HARD DELETE (Dylan: "i didnt recreate all the items either.
// i deliberatly didnt. and so those that i didnt recreate gone"): every item
// WITHOUT a wild card is removed from the array entirely — no pools, no menu,
// no HUD, no dashboard row. The 23 survivors take their card identity AND the
// wild-card square icon, so old buncha item PNGs render NOWHERE.
// Effect wiring keys on the original ids, so the 23 mechanics are untouched.
import { WILD_CARD_BY_ITEM, wildCardIconPath } from './wildCards';
for (let _i = BUNCHA_BULLSHIIII.length - 1; _i >= 0; _i--) {
  const it = BUNCHA_BULLSHIIII[_i];
  const wc = WILD_CARD_BY_ITEM[it.id];
  if (wc) {
    it.name = wc.name;
    it.iconUrl = wildCardIconPath(it.id) ?? it.iconUrl;
  } else {
    BUNCHA_BULLSHIIII.splice(_i, 1);   // gone with the buncha era ("by by")
  }
}

// --- Helper Functions ---

/** Get all active bullshiiii (excludes shelved) */
export function getActiveBullshiiii(): BullshiiiiItem[] {
  return BUNCHA_BULLSHIIII.filter(item => item.status === 'active');
}

/** Get bullshiiii by rarity */
export function getBullshiiiiByRarity(rarity: ItemRarity): BullshiiiiItem[] {
  return getActiveBullshiiii().filter(item => item.rarity === rarity);
}

/** Get bullshiiii by synergy tag */
export function getBullshiiiiBySynergy(synergy: SynergyTag): BullshiiiiItem[] {
  return getActiveBullshiiii().filter(item => item.synergy === synergy);
}

/** Get starter pool bullshiiii */
export function getStarterPoolBullshiiii(): BullshiiiiItem[] {
  return getActiveBullshiiii().filter(item => item.starterPool);
}

/** Get bullshiiii by ID */
export function getBullshiiiiById(id: string): BullshiiiiItem | undefined {
  return BUNCHA_BULLSHIIII.find(item => item.id === id);
}

/**
 * BETA 21.75 — read an item's editable effect magnitude from its `tuning` block,
 * falling back to the hardcoded default if the item hasn't been migrated / isn't
 * overridden. Reads the LIVE BUNCHA_BULLSHIIII object (the one initBalanceData
 * mutates with dashboard overrides), so a dashboard edit flows to handlers with
 * zero extra wiring. The fallback guarantees byte-identical behavior pre-migration.
 */
export function tuneOf(id: string, key: string, fallback: number): number {
  const t = getBullshiiiiById(id)?.tuning;
  return t && typeof t[key] === 'number' ? t[key] : fallback;
}

/** Get bullshiiii that require weapon creation */
export function getBullshiiiiNeedingWeapons(): BullshiiiiItem[] {
  return BUNCHA_BULLSHIIII.filter(item => item.hasWeapon);
}

/**
 * Roll a random bullshiiii — Megabonk luck-weighted rarity formula.
 *
 * BETA 19.0 — Switched from flat additive to geometric formula used in
 * Megabonk:
 *   S = ln(luck/100 + 1) × 1.5
 *   W_i = W_base[i] × 1.5^(-(numTiers - 1 - i) × S)
 * Higher luck reduces common weight, so legendary's relative share
 * climbs. At luck = 0 → 76/16/6/1.6%. At luck = 100 → 63/20/13/4.7%.
 * At luck = 500 → 44/22/21/13%.
 *
 * Uncommon tier is dropped (no active items there). Remaining 4 tiers
 * match Megabonk's structure: Common / Rare / Epic / Legendary.
 */
export function rollRandomBullshiiii(luckModifier: number = 0, _unlockedIds?: Set<string>, _source?: string): BullshiiiiItem {
  // BETA 19.66 — PSBOT cycle-30az: my 19.64 weights [50,25,15,8,2] actually
  // produced [39,7,50,2,0] in live data. Rare jumped to 50% because the
  // retry loop (dedupe + ownership filter) kept landing on rare — which
  // has the LARGEST item pool (18 items) vs uncommon (only 2 items).
  // After Cowboy picked 2 uncommons, every uncommon roll re-rolled and
  // skewed rare. Pool sizes per rarity:
  //   common: 16   uncommon: 2   rare: 18   epic: 10   legendary: 6
  // New weights MATCH pool ratios so retries don't pile-up on rare:
  //   common 60   uncommon 10   rare 20   epic 8   legendary 2
  // Long-term fix: add more uncommon items to the pool (content work).
  // BETA 20.39 — uncommon/epic are RETIRED tiers (0 active items live there
  // after the 3-tier collapse). They were still carrying weight (uncommon 4 +
  // epic 10 = ~14% of rolls) that silently fell back to common. Per Dylan:
  // shift that weight to rare + legendary so more rares/legendaries circulate.
  // Now matches the main chest table (_UNIFIED_CHEST_WEIGHTS): 60/0/33/0/7.
  // Active pool sizes (verified by parser): common 10, rare 10, legendary 4.
  const baseWeights: Record<ItemRarity, number> = {
    common:    60,
    uncommon:   0,    // RETIRED tier — 0 active items
    rare:      33,    // was 24 — absorbed the dead uncommon/epic weight
    epic:       0,    // RETIRED tier — 0 active items
    legendary:  7,    // was 2 — more legendaries circulate (Dylan-approved 20.39)
  };

  const luckArg = Math.max(0, luckModifier) / 100 + 1;
  const S = Math.log(luckArg) * 1.5;

  // BETA 19.64 — Added 'uncommon' to tiers (was MISSING entirely, hence
  // uncommon items never rolled).
  const tiers: ItemRarity[] = ['common', 'uncommon', 'rare', 'epic', 'legendary'];
  const tierWeights: Partial<Record<ItemRarity, number>> = {};
  for (let i = 0; i < tiers.length; i++) {
    const distFromTop = tiers.length - 1 - i;
    tierWeights[tiers[i]] = baseWeights[tiers[i]] * Math.pow(1.5, -distFromTop * S);
  }

  const totalWeight = Object.values(tierWeights).reduce((a, b) => a + (b ?? 0), 0);
  let roll = rollRng() * totalWeight;

  let selectedRarity: ItemRarity = 'common';
  for (const tier of tiers) {
    const w = tierWeights[tier] ?? 0;
    roll -= w;
    if (roll <= 0) { selectedRarity = tier; break; }
  }

  const rarityPool = getBullshiiiiByRarity(selectedRarity);
  if (rarityPool.length === 0) {
    const fallback = getBullshiiiiByRarity('common');
    return fallback[Math.floor(rollRng() * fallback.length)];
  }
  return rarityPool[Math.floor(rollRng() * rarityPool.length)];
}

// --- Compatibility Shims (for bunchaBullshiiiiSystem.ts v3) ---

export type ItemSource = 'chest' | 'vendor' | 'shrine' | 'boss_drop' | 'elite_drop';

/**
 * Get items available from a specific source.
 * Since the UI data format doesn't have per-item source lists,
 * all active items are available from all sources.
 */
export function getBullshiiiiBySource(_source: ItemSource): BullshiiiiItem[] {
  return getActiveBullshiiii();
}
